import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import { Colors } from "../../utils/colors";
import { useTheme } from "../../hooks/useTheme";
import {
  AccountData,
  Follower,
  FormInputType,
  SavedFormQuestion,
  SavedFormQuestionState,
  SavedFormResponse,
} from "@markit/common.types";
import SingleCustomData from "./SingleCustomData";
import EssentialDataResponse from "./EssentialDataResponse";
import { HorizontalDivider } from "../Dividers/HorizontalDivider";

type SingleCustomDataItemProps = {
  savedQuestion: SavedFormQuestion;
  formResponse: SavedFormResponse | undefined;
  userData: AccountData;
  followerData: Follower | undefined;
  pressable?: boolean;
  lastItem?: boolean;
  detailView?: boolean;
};

const SingleCustomDataItem = memo(function SingleCustomDataItemFn(
  props: SingleCustomDataItemProps
) {
  const {
    savedQuestion,
    formResponse,
    userData,
    followerData,
    pressable,
    lastItem,
    detailView,
  } = props;

  const { theme } = useTheme();
  const [singleCustomDataOpen, setSingleCustomDataOpen] = useState(false);
  const [localFormResponse, setLocalFormResponse] = useState<
    SavedFormResponse | undefined
  >();

  useEffect(() => {
    if (formResponse) {
      setLocalFormResponse(formResponse);
    }
  }, [formResponse]);

  const styles = {
    subSectionContainer: {
      marginInline: 14,
      gap: 24,
    },
  };

  const isInactiveQuestion = useMemo(
    () => savedQuestion.state === SavedFormQuestionState.INACTIVE,
    [savedQuestion.state]
  );

  const singleCustomDataOnPress = useCallback(() => {
    setSingleCustomDataOpen(true);
  }, []);

  const closeSingleCustomData = useCallback(() => {
    setSingleCustomDataOpen(false);
  }, []);

  return (
    <>
      {singleCustomDataOpen ? (
        <SingleCustomData
          savedQuestion={savedQuestion}
          formResponse={localFormResponse}
          setFormResponse={setLocalFormResponse}
          userData={userData}
          followerData={followerData}
          closeSingleCustomData={closeSingleCustomData}
        />
      ) : null}
      <div>
        <div
          className="AlignedRowSpaced"
          style={{ ...styles.subSectionContainer }}
        >
          <div className="ColumnNormal" style={{ flex: 1, gap: 4 }}>
            <div className="AlignedRow" style={{ gap: 7 }}>
              {savedQuestion.formInput.inputType === FormInputType.EMAIL ? (
                <Icon
                  icon={"ion:mail-open"}
                  height={15}
                  style={{ color: theme.SubText.color }}
                />
              ) : null}
              <span
                className={
                  !detailView
                    ? "TextOverflowEllipsis bodySubtext"
                    : "bodySubtext"
                }
              >
                {savedQuestion.formInput.label}
              </span>
            </div>
            <EssentialDataResponse
              formResponse={localFormResponse}
              userFullName={userData.fullName}
              copyable
              detailView={detailView ?? false}
            />
            {isInactiveQuestion ? (
              <span style={{ fontSize: 14, color: Colors.ORANGE1 }}>
                Inactive Question
              </span>
            ) : null}
          </div>
          {pressable ? (
            <Icon
              icon="mdi:chevron-right"
              height={26}
              style={{ color: theme.SubText.color, cursor: "pointer" }}
              onClick={pressable ? singleCustomDataOnPress : () => {}}
            />
          ) : null}
        </div>
        {!lastItem ? <HorizontalDivider altMargin={14} /> : null}
      </div>
    </>
  );
});

export default SingleCustomDataItem;
